import React from "react";
import {
  VitalSigns,
  Medication,
  Allergy,
  CarePlan,
  Appointment,
  Provider,
  HealthRecord,
} from "../types/patient";

const STORAGE_KEYS = {
  VITAL_SIGNS: "patient_vital_signs",
  MEDICATIONS: "patient_medications",
  ALLERGIES: "patient_allergies",
  CARE_PLANS: "patient_care_plans",
  APPOINTMENTS: "patient_appointments",
  PROVIDERS: "patient_providers",
  HEALTH_RECORDS: "patient_health_records",
  PENDING_CHANGES: "patient_pending_changes",
  LAST_SYNC: "patient_last_sync",
  OFFLINE_MODE: "patient_offline_mode",
} as const;

/** Clinical entity keys that must never hold PHI in localStorage. */
const CLINICAL_ENTITY_KEYS = [
  STORAGE_KEYS.VITAL_SIGNS,
  STORAGE_KEYS.MEDICATIONS,
  STORAGE_KEYS.ALLERGIES,
  STORAGE_KEYS.CARE_PLANS,
  STORAGE_KEYS.APPOINTMENTS,
  STORAGE_KEYS.PROVIDERS,
  STORAGE_KEYS.HEALTH_RECORDS,
] as const;

/** Keys that may contain clinical PHI payloads (entities + pending queue). */
const CLINICAL_PHI_LOCAL_STORAGE_KEYS = [
  ...CLINICAL_ENTITY_KEYS,
  STORAGE_KEYS.PENDING_CHANGES,
] as const;

/** Non-PHI offline metadata keys (safe to keep in localStorage). */
const NON_PHI_METADATA_KEYS = [
  STORAGE_KEYS.LAST_SYNC,
  STORAGE_KEYS.OFFLINE_MODE,
] as const;

const LEGACY_MOCK_SEED_PURGE_KEY = "patient_clinical_no_mock_seed_v1";
/** One-time migration: remove live clinical PHI keys from localStorage. */
const CLINICAL_PHI_LOCAL_STORAGE_PURGE_KEY =
  "patient_clinical_phi_no_localStorage_v1";

export interface PendingChange {
  id: string;
  type: "create" | "update" | "delete";
  entityType: string;
  data: any;
  timestamp: Date;
  retryCount: number;
}

export interface OfflineStorageState {
  isOnline: boolean;
  lastSync: Date | null;
  pendingChanges: PendingChange[];
  hasUnsyncedData: boolean;
}

/**
 * Clinical PHI lives in memory only for the page session.
 * Never persist clinical payloads or pending PHI to localStorage.
 */
class OfflineStorageManager {
  private isOnline: boolean = navigator.onLine;
  private listeners: Set<(state: OfflineStorageState) => void> = new Set();

  private vitalSigns: VitalSigns[] = [];
  private medications: Medication[] = [];
  private allergies: Allergy[] = [];
  private carePlans: CarePlan[] = [];
  private appointments: Appointment[] = [];
  private providers: Provider[] = [];
  private healthRecords: HealthRecord[] = [];
  private pendingChanges: PendingChange[] = [];

  constructor() {
    this.setupEventListeners();
    this.initializeStorage();
  }

  private setupEventListeners() {
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  private initializeStorage() {
    // Remove any previously persisted clinical PHI (entities + pending queue).
    this.purgeClinicalPhiFromLocalStorage();

    // Ensure non-PHI metadata keys exist; never seed clinical keys into localStorage.
    NON_PHI_METADATA_KEYS.forEach((key) => {
      if (!localStorage.getItem(key)) {
        if (key === STORAGE_KEYS.LAST_SYNC) {
          // leave unset until a real sync writes it
          return;
        }
        localStorage.setItem(key, JSON.stringify(false));
      }
    });
  }

  private handleOnline = () => {
    this.isOnline = true;
    this.notifyListeners();
    this.syncPendingChanges();
  };

  private handleOffline = () => {
    this.isOnline = false;
    this.notifyListeners();
  };

  private notifyListeners() {
    const state = this.getOfflineState();
    this.listeners.forEach((listener) => listener(state));
  }

  public getOfflineState(): OfflineStorageState {
    const pendingChanges = this.getPendingChanges();
    const lastSync = this.getLastSync();

    return {
      isOnline: this.isOnline,
      lastSync,
      pendingChanges,
      hasUnsyncedData: pendingChanges.length > 0,
    };
  }

  public subscribe(listener: (state: OfflineStorageState) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Clinical data — memory only (never localStorage)
  public saveVitalSigns(vitalSigns: VitalSigns[]): void {
    this.vitalSigns = vitalSigns;
  }

  public getVitalSigns(): VitalSigns[] {
    return this.vitalSigns;
  }

  public saveMedications(medications: Medication[]): void {
    this.medications = medications;
  }

  public getMedications(): Medication[] {
    return this.medications;
  }

  public saveAllergies(allergies: Allergy[]): void {
    this.allergies = allergies;
  }

  public getAllergies(): Allergy[] {
    return this.allergies;
  }

  public saveCarePlans(carePlans: CarePlan[]): void {
    this.carePlans = carePlans;
  }

  public getCarePlans(): CarePlan[] {
    return this.carePlans;
  }

  public saveAppointments(appointments: Appointment[]): void {
    this.appointments = appointments;
  }

  public getAppointments(): Appointment[] {
    return this.appointments;
  }

  public saveProviders(providers: Provider[]): void {
    this.providers = providers;
  }

  public getProviders(): Provider[] {
    return this.providers;
  }

  public saveHealthRecords(healthRecords: HealthRecord[]): void {
    this.healthRecords = healthRecords;
  }

  public getHealthRecords(): HealthRecord[] {
    return this.healthRecords;
  }

  /**
   * Queue a pending change in memory only.
   * Clinical PHI payloads are never written to localStorage.
   */
  public addPendingChange(
    change: Omit<PendingChange, "id" | "timestamp" | "retryCount">
  ): void {
    const pendingChange: PendingChange = {
      ...change,
      id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      retryCount: 0,
    };

    this.pendingChanges.push(pendingChange);
    this.notifyListeners();
  }

  public getPendingChanges(): PendingChange[] {
    return [...this.pendingChanges];
  }

  public removePendingChange(changeId: string): void {
    this.pendingChanges = this.pendingChanges.filter(
      (change) => change.id !== changeId
    );
    this.notifyListeners();
  }

  public updatePendingChangeRetryCount(
    changeId: string,
    retryCount: number
  ): void {
    const changeIndex = this.pendingChanges.findIndex(
      (change) => change.id === changeId
    );

    if (changeIndex !== -1) {
      this.pendingChanges[changeIndex] = {
        ...this.pendingChanges[changeIndex],
        retryCount,
      };
      this.notifyListeners();
    }
  }

  // Sync management — lastSync is non-PHI metadata
  public setLastSync(date: Date): void {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SYNC, date.toISOString());
    } catch (error) {
      console.error("Failed to set last sync:", error);
    }
  }

  public getLastSync(): Date | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
      return data ? new Date(data) : null;
    } catch (error) {
      console.error("Failed to get last sync:", error);
      return null;
    }
  }

  /**
   * Attempt to sync pending clinical changes to the server.
   *
   * Server write sync is not implemented. This is intentionally a no-op that
   * leaves the pending queue intact — never dequeue on simulated success.
   * Callers must not treat resolution as a successful sync.
   */
  public async syncPendingChanges(): Promise<{
    synced: false;
    reason: "server_sync_not_implemented";
    pendingCount: number;
  }> {
    const pendingChanges = this.getPendingChanges();
    return {
      synced: false,
      reason: "server_sync_not_implemented",
      pendingCount: pendingChanges.length,
    };
  }

  /** Clear in-memory clinical PHI and remove any leftover clinical localStorage keys. */
  public clearAllData(): void {
    try {
      this.vitalSigns = [];
      this.medications = [];
      this.allergies = [];
      this.carePlans = [];
      this.appointments = [];
      this.providers = [];
      this.healthRecords = [];
      this.pendingChanges = [];

      CLINICAL_PHI_LOCAL_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
      });
      NON_PHI_METADATA_KEYS.forEach((key) => {
        localStorage.removeItem(key);
      });
      this.notifyListeners();
    } catch (error) {
      console.error("Failed to clear all data:", error);
    }
  }

  /**
   * Remove live clinical PHI keys from localStorage (entities + pending queue).
   * Broader than the legacy mock-seed wipe: deletes keys rather than writing [].
   * Idempotent via CLINICAL_PHI_LOCAL_STORAGE_PURGE_KEY.
   */
  public purgeClinicalPhiFromLocalStorage(): void {
    try {
      if (localStorage.getItem(CLINICAL_PHI_LOCAL_STORAGE_PURGE_KEY)) {
        // Still remove keys if somehow reintroduced after migration.
        CLINICAL_PHI_LOCAL_STORAGE_KEYS.forEach((key) => {
          localStorage.removeItem(key);
        });
        return;
      }
      CLINICAL_PHI_LOCAL_STORAGE_KEYS.forEach((key) => {
        localStorage.removeItem(key);
      });
      localStorage.setItem(CLINICAL_PHI_LOCAL_STORAGE_PURGE_KEY, "1");
      // Mark legacy mock purge done so older callers don't re-seed empty arrays.
      localStorage.setItem(LEGACY_MOCK_SEED_PURGE_KEY, "1");
      this.notifyListeners();
    } catch (error) {
      console.error("Failed to purge clinical PHI from localStorage:", error);
    }
  }

  /**
   * @deprecated Prefer purgeClinicalPhiFromLocalStorage.
   * Kept for callers/tests that still invoke the legacy mock-seed purge.
   * Now delegates to the broader clinical PHI localStorage purge.
   */
  public purgeLegacyMockClinicalSeed(): void {
    this.purgeClinicalPhiFromLocalStorage();
  }

  // Get storage usage info (non-PHI metadata only in localStorage)
  public getStorageInfo(): {
    used: number;
    available: number;
    percentage: number;
  } {
    try {
      let used = 0;
      NON_PHI_METADATA_KEYS.forEach((key) => {
        const data = localStorage.getItem(key);
        if (data) {
          used += data.length;
        }
      });

      const available = 5 * 1024 * 1024 - used; // 5MB - used
      const percentage = (used / (5 * 1024 * 1024)) * 100;

      return { used, available, percentage };
    } catch (error) {
      console.error("Failed to get storage info:", error);
      return { used: 0, available: 0, percentage: 0 };
    }
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageManager();

// Hook for React components
export const useOfflineStorage = () => {
  const [offlineState, setOfflineState] = React.useState<OfflineStorageState>(
    offlineStorage.getOfflineState()
  );

  React.useEffect(() => {
    const unsubscribe = offlineStorage.subscribe(setOfflineState);
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    ...offlineState,
    syncPendingChanges: () => offlineStorage.syncPendingChanges(),
    clearAllData: () => offlineStorage.clearAllData(),
    getStorageInfo: () => offlineStorage.getStorageInfo(),
  };
};
